import { ENDPIONTS, httpService } from "@api";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";

interface Props {
  callback?: () => void;
}

const OTHERS_BRANCHES = ["Qubanaha", "Ciyaartoyga", "Dugsiyada", "MCH"] as const;

interface SkippedRow {
  row: number;
  reason: string;
}

interface UploadResult {
  imported: number;
  skipped: SkippedRow[];
}

const UploadOthers = ({ callback }: Props) => {
  const { register, handleSubmit, errors } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit = async (data: any, e: any) => {
    setIsLoading(true);
    setResult(null);
    setErrorMsg(null);

    const formData = new FormData();
    formData.append("file", data.file[0]);
    formData.append("branchName", data.branchName);

    const res = await httpService(ENDPIONTS.uploadOthers).post(formData);

    if (res?.status === 200 || res?.status === 201) {
      setResult(res.data as UploadResult);
      callback?.();
      e.target.reset();
    } else {
      setErrorMsg(res?.data?.message ?? "Upload failed.");
    }

    setIsLoading(false);
  };

  const onDownload = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const sample = [
      { EmployeeId: "184", Name: "Sample Name One", Allowances: 1520000 },
      { EmployeeId: "186", Name: "Sample Name Two", Allowances: 2000000 },
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sample);
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "Others Upload Template.xlsx");
  };

  return (
    <>
      <h3>Upload Others</h3>
      <hr />
      <div className="d-flex justify-content-start mb-3">
        <a href="!#" style={{ fontSize: "12px" }} onClick={onDownload}>
          Click here to download the template
        </a>
      </div>
      <p className="text-muted" style={{ fontSize: "12px" }}>
        Expected columns: <b>EmployeeId</b>, <b>Name</b>, <b>Allowances</b>. Header row may be on row 1 or row 2
        (blank row 1 is supported).
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label htmlFor="branchName">Branch</label>
              <select
                name="branchName"
                id="branchName"
                className="form-control"
                ref={register({ required: true })}
                defaultValue=""
              >
                <option value="" disabled>
                  Select a branch...
                </option>
                {OTHERS_BRANCHES.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              <span className="text-danger">
                {errors.branchName && <span>This field is required</span>}
              </span>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col">
            <div className="form-group">
              <label htmlFor="file">Excel file</label>
              <input
                type="file"
                name="file"
                id="file"
                accept=".xlsx,.xls"
                className="form-control"
                ref={register({ required: true })}
              />
              <span className="text-danger">
                {errors.file && <span>This field is required</span>}
              </span>
            </div>
          </div>
        </div>

        <input
          type="submit"
          className="btn btn-primary mt-2"
          disabled={isLoading}
          value={isLoading ? "Please wait..." : "Upload"}
        />
      </form>

      {errorMsg && (
        <div className="alert alert-danger mt-3" role="alert">
          {errorMsg}
        </div>
      )}

      {result && (
        <div className="mt-3">
          <div className="alert alert-success" role="alert">
            Imported: <b>{result.imported}</b> employee(s).
            {result.skipped?.length > 0 && (
              <>
                {" "}
                Skipped: <b>{result.skipped.length}</b>.
              </>
            )}
          </div>
          {result.skipped?.length > 0 && (
            <details>
              <summary style={{ cursor: "pointer" }}>View skipped rows</summary>
              <table className="table table-sm mt-2">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.skipped.map((s, i) => (
                    <tr key={i}>
                      <td>{s.row}</td>
                      <td>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </>
  );
};

export default UploadOthers;
